import React, { useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BarChart3,
  BadgeCheck,
  Boxes,
  ClipboardList,
  FileSpreadsheet,
  LineChart,
  MessageCircleMore,
  PackageSearch,
  Save,
  Search,
  ShieldCheck,
  ShoppingCart,
  Users,
} from 'lucide-react';
import Button from '../../components/ui/Button';
import ContactForm from './components/ContactForm';

const demoHref = '/login';

const problemItems = [
  'messaggi incompleti',
  'codici prodotto scritti male',
  'prezzi da controllare',
  'quantità da ricontrollare',
  'ordini da copiare a mano',
  'errori tra listino pubblico e prezzo rivenditore',
  'tempo perso per rispondere sempre alle stesse domande',
  'difficoltà nel capire chi ha ordinato cosa',
];

const features = [
  {
    title: 'Listino sempre ordinato',
    description: 'Prodotti, codici SKU, prezzi pubblici e prezzi rivenditore in un’unica interfaccia semplice da usare.',
    icon: ClipboardList,
  },
  {
    title: 'Ricerca veloce per nome o SKU',
    description: 'Il rivenditore trova subito il prodotto giusto senza dover scorrere listini infiniti o chiederti conferme.',
    icon: Search,
  },
  {
    title: 'Carrello con totale automatico',
    description: 'Quantità, prodotti e importi vengono calcolati in automatico, riducendo errori e incomprensioni.',
    icon: ShoppingCart,
  },
  {
    title: 'Invio ordine via WhatsApp',
    description: 'L’ordine arriva già compilato, leggibile e pronto da gestire.',
    icon: MessageCircleMore,
  },
  {
    title: 'Sincronizzazione con Google Sheet',
    description: 'I prodotti possono essere collegati a un foglio Google, così il listino resta più facile da aggiornare.',
    icon: FileSpreadsheet,
  },
  {
    title: 'Autosalvataggio',
    description: 'Il lavoro non si perde se la pagina viene chiusa o ricaricata accidentalmente.',
    icon: Save,
  },
  {
    title: 'Statistiche e storico ordini',
    description: 'Puoi vedere ordini, fatturato, prodotti venduti, attività utenti e andamento generale.',
    icon: LineChart,
  },
  {
    title: 'Gestione utenti e venditori',
    description: 'Puoi creare utenti dedicati, distinguere ruoli e gestire accessi per rivenditori, venditori o amministratori.',
    icon: Users,
  },
];

const beforeItems = [
  'Ordini su WhatsApp',
  'Codici prodotto scritti a mano',
  'Prezzi da verificare',
  'Messaggi vocali e richieste sparse',
  'Errori sulle quantità',
  'Tempo perso a ricopiare tutto',
  'Nessuno storico chiaro',
];

const afterItems = [
  'Modulo ordine dedicato',
  'Ricerca prodotti per nome o SKU',
  'Prezzi rivenditore già corretti',
  'Carrello automatico',
  'Ordine ordinato via WhatsApp',
  'Meno errori manuali',
  'Statistiche e storico consultabili',
];

const targetItems = [
  'aziende con rivenditori',
  'produttori con listini riservati',
  'fornitori B2B',
  'grossisti',
  'brand con agenti o partner commerciali',
  'negozi con clienti ricorrenti',
  'aziende che ricevono ordini ripetitivi',
];

const personalizationItems = [
  'campi ordine personalizzati',
  'listini riservati',
  'ruoli diversi per utenti e venditori',
  'colonne prodotto specifiche',
  'esportazioni',
  'testi WhatsApp personalizzati',
  'collegamenti a fogli Google',
  'dashboard statistiche su misura',
];

const faqItems = [
  {
    question: 'Serve installare qualcosa?',
    answer: 'No, la WebApp si usa online da browser. Il rivenditore accede e compila l’ordine.',
  },
  {
    question: 'Posso usare i miei prodotti e i miei prezzi?',
    answer: 'Sì, il modulo viene adattato al tuo listino, ai tuoi prodotti e alle tue regole commerciali.',
  },
  {
    question: 'È un gestionale completo?',
    answer: 'No, ed è proprio questo il punto. È uno strumento verticale per raccogliere ordini in modo più semplice, veloce e ordinato.',
  },
  {
    question: 'I rivenditori possono avere prezzi riservati?',
    answer: 'Sì, il modulo può mostrare prezzi dedicati ai rivenditori o logiche personalizzate.',
  },
  {
    question: 'Posso provarlo prima?',
    answer: 'Sì, dalla landing è presente il pulsante per provare la demo.',
  },
  {
    question: 'Si può personalizzare?',
    answer: 'Sì, campi, testi, flussi, listini e funzioni possono essere adattati in base al processo aziendale.',
  },
];

const ListBlock = ({ items, tone = 'blue' }) => {
  const toneClasses = tone === 'green'
    ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
    : 'border-blue-200 bg-blue-50 text-blue-700';

  return (
    <ul className="space-y-3">
      {items?.map((item) => (
        <li key={item} className="flex items-start gap-3">
          <span className={`mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-sm font-semibold ${toneClasses}`}>
            {tone === 'green' ? '✓' : '•'}
          </span>
          <span className="text-sm leading-6 text-slate-700 sm:text-base">{item}</span>
        </li>
      ))}
    </ul>
  );
};

const MetricCard = ({ label, value, accent }) => (
  <div className={`rounded-2xl border px-4 py-4 shadow-sm ${accent}`}>
    <div className="text-2xl font-bold text-slate-900 sm:text-3xl">{value}</div>
    <div className="mt-1 text-sm text-slate-600">{label}</div>
  </div>
);

const ModuloOrdineRivenditorePage = () => {
  const scrollToContact = useCallback(() => {
    const element = document.getElementById('contact-form');
    if (!element) return;

    const targetPosition = element.getBoundingClientRect().top + window.scrollY - 80;
    const startPosition = window.scrollY;
    const distance = targetPosition - startPosition;
    const duration = 1200;
    let start = null;

    const easeInOutCubic = (t) => (
      t < 0.5 ? 4 * t * t * t : 1 - (((-2 * t) + 2) ** 3) / 2
    );

    const animation = (currentTime) => {
      if (start === null) start = currentTime;
      const timeElapsed = currentTime - start;
      const progress = Math.min(timeElapsed / duration, 1);
      const ease = easeInOutCubic(progress);

      window.scrollTo(0, startPosition + distance * ease);

      if (timeElapsed < duration) {
        window.requestAnimationFrame(animation);
      }
    };

    window.requestAnimationFrame(animation);
  }, []);

  return (
    <>
      <Helmet>
        <title>Modulo Ordine Rivenditore | WebApp per ordini da rivenditori</title>
        <meta
          name="description"
          content="Una WebApp su misura per raccogliere ordini da rivenditori, agenti e partner commerciali senza WhatsApp disordinati, Excel, errori di prezzo o lavoro manuale."
        />
      </Helmet>

      <div className="overflow-x-hidden bg-slate-50 text-slate-900">
        <section className="relative isolate overflow-hidden border-b border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(37,99,235,0.12),_transparent_32%),linear-gradient(180deg,_#ffffff_0%,_#f8fbff_52%,_#f8fafc_100%)]">
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-blue-300 to-transparent" />
          <div className="container px-4 py-6 sm:px-6 lg:px-8">
            <div className="flex flex-col gap-4 rounded-[28px] border border-slate-200/90 bg-white/90 px-5 py-4 shadow-[0_20px_45px_-30px_rgba(15,23,42,0.2)] backdrop-blur sm:flex-row sm:items-center sm:justify-between sm:px-6 sm:py-5">
              <div className="min-w-0">
                <p className="text-sm font-bold tracking-[0.14em] text-blue-700 sm:text-base">sviluppoweb.app</p>
                <p className="mt-1 text-sm font-medium text-slate-600 sm:text-base">WebApp verticali per processi reali</p>
              </div>
              <Button
                asChild
                size="lg"
                className="w-full border border-blue-200 bg-blue-600 text-white shadow-lg shadow-blue-600/20 hover:bg-blue-700 sm:w-auto"
                data-track-cta="modulo-ordine-rivenditore-header-demo"
              >
                <Link to={demoHref}>Prova la demo</Link>
              </Button>
            </div>

            <div className="grid gap-12 py-12 lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:py-20">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-700">
                  <BadgeCheck className="h-4 w-4" />
                  Meno errori. Meno tempo perso. Meno ordini da ricostruire a mano.
                </div>
                <h1 className="mt-6 max-w-3xl text-4xl font-bold leading-tight text-slate-950 sm:text-5xl lg:text-6xl">
                  Fai ordinare i tuoi rivenditori senza WhatsApp, Excel e messaggi sparsi.
                </h1>
                <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-600 sm:text-xl">
                  Un modulo ordini semplice e personalizzato dove i tuoi rivenditori scelgono i prodotti, vedono i prezzi corretti, compilano il carrello e ti inviano l’ordine in modo chiaro, ordinato e tracciabile.
                </p>

                <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                  <Button asChild size="xl" className="shadow-lg shadow-blue-600/20" data-track-cta="modulo-ordine-rivenditore-demo">
                    <Link to={demoHref}>
                      Prova la demo
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Link>
                  </Button>
                  <Button variant="outline" size="xl" data-track-cta="modulo-ordine-rivenditore-form" onClick={scrollToContact}>
                    Richiedi informazioni
                  </Button>
                  <Button asChild variant="ghost" size="xl" className="justify-start px-0 text-blue-700 hover:bg-transparent hover:text-blue-800" data-track-cta="modulo-ordine-rivenditore-scroll-problema">
                    <a href="#problema">Guarda cosa risolve</a>
                  </Button>
                </div>

                <div className="mt-10 grid gap-4 sm:grid-cols-3">
                  <MetricCard label="canali sparsi sostituiti da un flusso unico" value="1" accent="border-blue-200 bg-blue-50" />
                  <MetricCard label="prezzi e quantità gestiti in modo coerente" value="0" accent="border-emerald-200 bg-emerald-50" />
                  <MetricCard label="passaggi manuali da rifare per ogni ordine" value="-10/15 min" accent="border-slate-200 bg-white" />
                </div>
              </div>

              <div className="relative">
                <div className="absolute -left-8 top-8 hidden h-24 w-24 rounded-full bg-blue-200/40 blur-3xl lg:block" />
                <div className="absolute -right-6 bottom-8 hidden h-28 w-28 rounded-full bg-emerald-200/50 blur-3xl lg:block" />
                <div className="relative overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_30px_90px_-36px_rgba(15,23,42,0.34)]">
                  <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-100 px-4 py-3">
                    <span className="h-3 w-3 rounded-full bg-rose-400" />
                    <span className="h-3 w-3 rounded-full bg-amber-400" />
                    <span className="h-3 w-3 rounded-full bg-emerald-400" />
                    <div className="ml-3 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-500">
                      modulo-ordine-rivenditore.demo
                    </div>
                  </div>

                  <div className="grid gap-4 p-4 lg:grid-cols-[1.1fr_0.9fr]">
                    <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-semibold text-slate-900">Listino rivenditore</p>
                          <p className="text-xs text-slate-500">Ricerca per nome o SKU</p>
                        </div>
                        <div className="rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700">
                          247 prodotti
                        </div>
                      </div>

                      <div className="mt-4 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-400">
                        Cerca: “sku 230 / detergente / linea green”
                      </div>

                      <div className="mt-4 space-y-3">
                        {[
                          ['SKU-230', 'Detergente Multiuso', '€12,50', '€9,20'],
                          ['SKU-118', 'Starter Kit Rivenditore', '€49,00', '€39,50'],
                          ['SKU-422', 'Espositore Banco', '€89,00', '€72,00'],
                        ]?.map(([sku, name, publicPrice, resellerPrice]) => (
                          <div key={sku} className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">{sku}</p>
                                <p className="mt-1 font-semibold text-slate-900">{name}</p>
                              </div>
                              <button className="rounded-xl bg-blue-600 px-3 py-2 text-xs font-semibold text-white">
                                Aggiungi
                              </button>
                            </div>
                            <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                              <div className="rounded-xl bg-slate-50 px-3 py-2">
                                <div className="text-slate-500">Prezzo pubblico</div>
                                <div className="font-semibold text-slate-900">{publicPrice}</div>
                              </div>
                              <div className="rounded-xl bg-emerald-50 px-3 py-2">
                                <div className="text-emerald-700">Prezzo rivenditore</div>
                                <div className="font-semibold text-slate-900">{resellerPrice}</div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-100">
                        <img
                          src="/modulo1.png"
                          alt="Anteprima modulo ordine rivenditore con listino prodotti e carrello"
                          className="h-full w-full object-cover"
                        />
                      </div>
                      <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-semibold text-slate-900">Carrello ordine</p>
                            <p className="text-xs text-slate-500">Totali automatici e invio ordinato</p>
                          </div>
                          <div className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
                            Autosalvato
                          </div>
                        </div>

                        <div className="mt-4 space-y-3">
                          {[
                            ['SKU-230', '12 pz', '€110,40'],
                            ['SKU-118', '2 pz', '€79,00'],
                            ['SKU-422', '1 pz', '€72,00'],
                          ]?.map(([sku, qty, total]) => (
                            <div key={sku} className="flex items-center justify-between rounded-2xl bg-slate-50 px-3 py-3 text-sm">
                              <div>
                                <div className="font-semibold text-slate-900">{sku}</div>
                                <div className="text-slate-500">{qty}</div>
                              </div>
                              <div className="font-semibold text-slate-900">{total}</div>
                            </div>
                          ))}
                        </div>

                        <div className="mt-4 rounded-2xl bg-blue-600 px-4 py-4 text-white">
                          <div className="flex items-center justify-between text-sm text-blue-100">
                            <span>Totale ordine</span>
                            <span>15 righe</span>
                          </div>
                          <div className="mt-1 text-3xl font-bold">€261,40</div>
                          <div className="mt-3 flex items-center gap-2 text-sm font-medium">
                            <MessageCircleMore className="h-4 w-4" />
                            Invia ordine via WhatsApp
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="problema" className="container px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.24em] text-blue-700">Problema</p>
              <h2 className="mt-4 text-3xl font-bold leading-tight text-slate-950 sm:text-4xl">
                Ogni ordine gestito male ti costa più di quanto pensi.
              </h2>
              <p className="mt-6 text-lg leading-8 text-slate-600">
                Quando un rivenditore ordina via WhatsApp, telefono o email, sembra tutto semplice.
                Ma poi iniziano i problemi:
              </p>
              <p className="mt-6 rounded-3xl border border-slate-200 bg-white px-5 py-5 text-base leading-7 text-slate-700 shadow-sm">
                Il vero costo non è solo il tempo. È il caos operativo che si accumula ogni settimana.
              </p>
            </div>
            <div className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_24px_70px_-32px_rgba(15,23,42,0.25)]">
              <ListBlock items={problemItems} />
            </div>
          </div>
        </section>

        <section id="soluzione" className="border-y border-slate-200 bg-white">
          <div className="container px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
            <div className="max-w-3xl">
              <p className="text-sm font-semibold uppercase tracking-[0.24em] text-emerald-700">Soluzione</p>
              <h2 className="mt-4 text-3xl font-bold leading-tight text-slate-950 sm:text-4xl">
                Un modulo ordini pensato per chi vende tramite rivenditori.
              </h2>
              <p className="mt-6 text-lg leading-8 text-slate-600">
                La WebApp permette ai tuoi rivenditori di entrare in un’area dedicata, cercare i prodotti, aggiungerli al carrello e inviarti l’ordine già strutturato.
              </p>
              <p className="mt-4 text-lg leading-8 text-slate-600">
                Tu non devi più ricostruire conversazioni, controllare manualmente ogni prezzo o inseguire informazioni mancanti.
              </p>
            </div>

            <div className="mt-12 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
              {features?.map((feature) => {
                const Icon = feature.icon;
                return (
                  <div key={feature.title} className="rounded-[26px] border border-slate-200 bg-slate-50 p-5 transition-transform duration-200 hover:-translate-y-1 hover:shadow-lg">
                    <div className="inline-flex rounded-2xl bg-blue-100 p-3 text-blue-700">
                      <Icon className="h-5 w-5" />
                    </div>
                    <h3 className="mt-4 text-lg font-semibold text-slate-900">{feature.title}</h3>
                    <p className="mt-3 text-sm leading-7 text-slate-600">{feature.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="container px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-[0.24em] text-blue-700">Prima / Dopo</p>
            <h2 className="mt-4 text-3xl font-bold leading-tight text-slate-950 sm:text-4xl">
              Prima ricevevi ordini da interpretare. Ora ricevi ordini pronti da lavorare.
            </h2>
          </div>

          <div className="mt-12 grid gap-6 lg:grid-cols-2">
            <div className="rounded-[28px] border border-rose-200 bg-rose-50 p-6">
              <div className="flex items-center gap-3">
                <div className="rounded-2xl bg-white p-3 text-rose-600 shadow-sm">
                  <Boxes className="h-5 w-5" />
                </div>
                <h3 className="text-2xl font-semibold text-slate-900">Prima</h3>
              </div>
              <div className="mt-6">
                <ListBlock items={beforeItems} />
              </div>
            </div>

            <div className="rounded-[28px] border border-emerald-200 bg-emerald-50 p-6">
              <div className="flex items-center gap-3">
                <div className="rounded-2xl bg-white p-3 text-emerald-600 shadow-sm">
                  <ShieldCheck className="h-5 w-5" />
                </div>
                <h3 className="text-2xl font-semibold text-slate-900">Dopo</h3>
              </div>
              <div className="mt-6">
                <ListBlock items={afterItems} tone="green" />
              </div>
            </div>
          </div>
        </section>

        <section className="border-y border-slate-200 bg-[linear-gradient(180deg,_#eff6ff_0%,_#ffffff_100%)]">
          <div className="container px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
            <div className="grid gap-10 lg:grid-cols-[1fr_0.95fr] lg:items-start">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.24em] text-blue-700">Tempo e margine</p>
                <h2 className="mt-4 text-3xl font-bold leading-tight text-slate-950 sm:text-4xl">
                  Non è solo una WebApp. È tempo operativo che recuperi ogni settimana.
                </h2>
                <p className="mt-6 text-lg leading-8 text-slate-600">
                  Se ogni ordine ti porta via anche solo 10 o 15 minuti tra messaggi, controlli, conferme e copia-incolla, a fine mese stai perdendo ore di lavoro su attività che potrebbero essere automatizzate.
                </p>
                <p className="mt-4 text-lg leading-8 text-slate-600">
                  E più crescono i rivenditori, più cresce il caos.
                </p>
                <p className="mt-4 text-lg leading-8 text-slate-600">
                  Questa WebApp serve proprio a questo: trasformare il modo in cui raccogli gli ordini, senza costringere la tua azienda a usare gestionali complessi o piattaforme rigide.
                </p>
              </div>

              <div className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_24px_70px_-32px_rgba(15,23,42,0.25)]">
                <div className="flex items-center gap-3">
                  <div className="rounded-2xl bg-emerald-100 p-3 text-emerald-700">
                    <PackageSearch className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-lg font-semibold text-slate-900">Per chi è perfetta</p>
                    <p className="text-sm text-slate-500">B2B reale. Processi ripetitivi. Listini dedicati.</p>
                  </div>
                </div>
                <div className="mt-6">
                  <ListBlock items={targetItems} tone="green" />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="demo" className="container px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <div className="grid gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:items-center">
            <div className="overflow-hidden rounded-[30px] border border-slate-200 bg-slate-950 shadow-[0_24px_70px_-32px_rgba(15,23,42,0.25)]">
              <img
                src="/modulo2.png"
                alt="Anteprima demo modulo ordine rivenditore"
                className="w-full object-contain"
              />
            </div>

            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.24em] text-blue-700">Demo</p>
              <h2 className="mt-4 text-3xl font-bold leading-tight text-slate-950 sm:text-4xl">
                Vuoi vedere come funziona?
              </h2>
              <p className="mt-6 text-lg leading-8 text-slate-600">
                La demo ti permette di provare il flusso reale: ricerca prodotto, aggiunta al carrello, calcolo totale e invio dell’ordine.
              </p>
              <div className="mt-8 rounded-[28px] border border-blue-200 bg-blue-50 p-5">
                <p className="text-sm font-semibold uppercase tracking-[0.2em] text-blue-700">Demo già presente nel progetto</p>
                <p className="mt-2 text-sm leading-7 text-slate-700">
                  Accesso demo con credenziali già esposte nella pagina login. Reindirizzamento su route esistente del progetto.
                </p>
              </div>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Button asChild size="xl" data-track-cta="modulo-ordine-rivenditore-demo">
                  <Link to={demoHref}>Prova la demo del modulo ordine</Link>
                </Button>
                <Button variant="outline" size="xl" data-track-cta="modulo-ordine-rivenditore-demo-info" onClick={scrollToContact}>
                  Richiedi informazioni
                </Button>
              </div>
            </div>
          </div>
        </section>

        <section className="border-y border-slate-200 bg-white">
          <div className="container px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
            <div className="grid gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:items-start">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.24em] text-emerald-700">Personalizzazione</p>
                <h2 className="mt-4 text-3xl font-bold leading-tight text-slate-950 sm:text-4xl">
                  Non è un software generico. Può essere adattato al tuo modo di lavorare.
                </h2>
                <p className="mt-6 text-lg leading-8 text-slate-600">
                  Ogni azienda ha listini, regole, prezzi, rivenditori e flussi diversi. Per questo il modulo può essere personalizzato in base al tuo processo reale.
                </p>
              </div>
              <div className="rounded-[28px] border border-slate-200 bg-slate-50 p-6">
                <ListBlock items={personalizationItems} tone="green" />
              </div>
            </div>
          </div>
        </section>

        <section className="container px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <div className="grid gap-10 lg:grid-cols-[1fr_0.9fr] lg:items-start">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.24em] text-blue-700">Fiducia</p>
              <h2 className="mt-4 text-3xl font-bold leading-tight text-slate-950 sm:text-4xl">
                Semplice per il rivenditore. Utile per l’azienda.
              </h2>
              <p className="mt-6 text-lg leading-8 text-slate-600">
                Il rivenditore non deve imparare un gestionale complicato. Entra, cerca, seleziona, invia.
              </p>
              <p className="mt-4 text-lg leading-8 text-slate-600">
                L’azienda riceve un ordine più chiaro, più completo e più facile da gestire.
              </p>
              <p className="mt-4 text-lg leading-8 text-slate-600">
                Questo è il punto: non aggiungere complessità, ma togliere attrito.
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm">
                <Users className="h-6 w-6 text-blue-700" />
                <h3 className="mt-4 text-lg font-semibold text-slate-900">Per rivenditore</h3>
                <p className="mt-2 text-sm leading-7 text-slate-600">Accesso diretto, ricerca rapida, carrello chiaro, nessun listino confuso.</p>
              </div>
              <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm">
                <BarChart3 className="h-6 w-6 text-emerald-700" />
                <h3 className="mt-4 text-lg font-semibold text-slate-900">Per azienda</h3>
                <p className="mt-2 text-sm leading-7 text-slate-600">Ordini leggibili, storico consultabile, meno verifiche, più controllo operativo.</p>
              </div>
            </div>
          </div>
        </section>

        <section id="contatto" className="border-y border-slate-200 bg-[linear-gradient(180deg,_#ffffff_0%,_#f8fafc_100%)]">
          <div className="container px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
            <div className="mx-auto max-w-3xl text-center">
              <p className="text-sm font-semibold uppercase tracking-[0.24em] text-blue-700">Contatto</p>
              <h2 className="mt-4 text-3xl font-bold leading-tight text-slate-950 sm:text-4xl">
                Vuoi capire se può funzionare anche per la tua azienda?
              </h2>
              <p className="mt-6 text-lg leading-8 text-slate-600">
                Raccontaci come ricevi oggi gli ordini dai tuoi rivenditori e ti diciamo se un modulo come questo può farti risparmiare tempo, errori e lavoro manuale.
              </p>
            </div>

            <div className="mx-auto mt-10 max-w-4xl">
              <ContactForm />
            </div>
          </div>
        </section>

        <section className="container px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.24em] text-emerald-700">FAQ</p>
              <h2 className="mt-4 text-3xl font-bold leading-tight text-slate-950 sm:text-4xl">
                Domande frequenti prima di partire.
              </h2>
            </div>
            <div className="space-y-4">
              {faqItems?.map((item) => (
                <details key={item.question} className="group rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-lg font-semibold text-slate-900">
                    {item.question}
                    <span className="text-2xl text-slate-400 transition-transform duration-200 group-open:rotate-45">+</span>
                  </summary>
                  <p className="mt-4 text-sm leading-7 text-slate-600 sm:text-base">{item.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="pb-16 sm:pb-20 lg:pb-24">
          <div className="container px-4 sm:px-6 lg:px-8">
            <div className="rounded-[32px] border border-slate-200 bg-slate-900 px-6 py-10 text-white shadow-[0_30px_90px_-36px_rgba(15,23,42,0.5)] sm:px-10">
              <div className="max-w-3xl">
                <h2 className="text-3xl font-bold leading-tight sm:text-4xl">
                  Se i tuoi rivenditori ordinano spesso, non dovresti gestirli a mano.
                </h2>
                <p className="mt-5 text-lg leading-8 text-slate-300">
                  Prova la demo oppure raccontaci come lavori oggi. Ti aiutiamo a capire se un modulo ordini personalizzato può farti risparmiare tempo, errori e lavoro operativo.
                </p>
              </div>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Button asChild size="xl" className="bg-white text-slate-950 hover:bg-slate-100" data-track-cta="modulo-ordine-rivenditore-final-demo">
                  <Link to={demoHref}>Prova la demo</Link>
                </Button>
                <Button variant="outline" size="xl" className="border-white/25 bg-white/5 text-white hover:bg-white/10" data-track-cta="modulo-ordine-rivenditore-final-form" onClick={scrollToContact}>
                  Richiedi informazioni
                </Button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ModuloOrdineRivenditorePage;
